/**
 * Zustand store da sessao da SPA web.
 *
 * Decisoes (docs/modules/web-v2-auth.md):
 * - Refresh token mora no cookie httpOnly emitido pelo apps/api.
 * - Access token mora apenas em memoria (este store) — vida util ~15min.
 * - NAO persiste em localStorage/sessionStorage. Reload re-popula via /me.
 * - Status 'unknown' ate bootstrap concluir; nunca renderizar conteudo
 *   protegido enquanto status nao for 'authenticated' ou 'unauthenticated'.
 */

import { create } from 'zustand';
import { DEFAULT_ONBOARDING_STATE, type OnboardingState } from '@proadv/types';
import { apiClient, type ApiError, type AllowedModule } from './api-client';
import { publish } from './broadcast';

export type AuthStatus =
  | 'unknown'
  | 'authenticating'
  | 'authenticated'
  | 'unauthenticated';

export interface PublicUser {
  id: string;
  email: string;
  name: string;
  oab?: string | null;
  status?: string;
  emailVerifiedAt?: string | null;
  mfaEnabled: boolean;
  isSuperAdmin?: boolean;
  createdAt?: string;
}

export interface TenantSummary {
  id: string;
  slug: string;
  name: string;
}

export interface TenantMembership {
  tenantId: string;
  slug: string;
  name: string;
  role: string;
}

export interface PendingMfa {
  challengeId: string;
  email: string;
  name?: string;
}

export type BillingBlockReason = 'past_due' | 'suspended' | 'canceled';

const KNOWN_BILLING_REASONS: readonly BillingBlockReason[] = [
  'past_due',
  'suspended',
  'canceled',
];

function normalizeBillingReason(raw: unknown): BillingBlockReason | null {
  return typeof raw === 'string' &&
    (KNOWN_BILLING_REASONS as readonly string[]).includes(raw)
    ? (raw as BillingBlockReason)
    : null;
}

export interface AuthState {
  status: AuthStatus;
  user: PublicUser | null;
  accessToken: string | null;
  tenantId: string | null;
  tenant: TenantSummary | null;
  isSuperAdmin: boolean;
  memberships: TenantMembership[];
  mfaPending: PendingMfa | null;
  /** role e modulos do membership ativo no tenant corrente */
  legacyRole: string | null;
  allowedModules: AllowedModule[] | null;
  /** Bloqueio por inadimplencia (vem de /me `billing`, ou forcado por um 402 em qualquer request). */
  billingBlocked: boolean;
  billingReason: BillingBlockReason | null;
  graceEndsAt: string | null;
  /** Estado do tour guiado de onboarding (indicadores da sidebar + modal). */
  onboarding: OnboardingState;
}

const initialState: AuthState = {
  status: 'unknown',
  user: null,
  accessToken: null,
  tenantId: null,
  tenant: null,
  isSuperAdmin: false,
  memberships: [],
  mfaPending: null,
  legacyRole: null,
  allowedModules: null,
  billingBlocked: false,
  billingReason: null,
  graceEndsAt: null,
  onboarding: DEFAULT_ONBOARDING_STATE,
};

interface HydrateInput {
  user: PublicUser;
  accessToken: string;
  tenantId: string | null;
  isSuperAdmin?: boolean;
}

interface AuthActions {
  bootstrap(): Promise<void>;
  signIn(email: string, password: string): Promise<{ mfaRequired: boolean }>;
  verifyMfa(code: string): Promise<void>;
  signOut(reason?: string, opts?: { broadcast?: boolean }): Promise<void>;
  switchTenant(tenantId: string): Promise<void>;
  setAccessToken(token: string): void;
  hydrateFromTokens(input: HydrateInput): void;
  hardReset(reason?: string): void;
  /** Forca o bloqueio a partir de um 402 SUBSCRIPTION_BLOCKED vindo de qualquer request (ver api-client hooks). */
  setBillingBlocked(reason: string | null): void;
  /** Atualiza o estado de onboarding local (apos PATCH /me/onboarding bem-sucedido). */
  setOnboarding(onboarding: OnboardingState): void;
}

type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>((set, get) => ({
  ...initialState,

  async bootstrap() {
    if (get().status === 'authenticating') return;
    set({ status: 'authenticating' });
    try {
      const me = await apiClient.me();
      set({
        status: 'authenticated',
        user: me.user,
        accessToken: me.accessToken,
        tenantId: me.tenantId ?? null,
        tenant: me.tenant ?? null,
        isSuperAdmin: Boolean(me.isSuperAdmin),
        memberships: me.memberships ?? [],
        mfaPending: null,
        legacyRole: me.currentMembership?.legacyRole ?? null,
        allowedModules: me.currentMembership?.allowedModules ?? null,
        billingBlocked: me.billing?.blocked ?? false,
        billingReason: normalizeBillingReason(me.billing?.reason ?? null),
        graceEndsAt: me.billing?.graceEndsAt ?? null,
        onboarding: me.onboarding ?? DEFAULT_ONBOARDING_STATE,
      });
    } catch (err) {
      const status = (err as ApiError | undefined)?.status;
      if (status === 401 || status === 403) {
        set({ ...initialState, status: 'unauthenticated' });
      } else {
        // Erro de rede / 5xx — mantem 'unknown' para o caller decidir UX.
        set({ status: 'unknown' });
        throw err;
      }
    }
  },

  async signIn(email, password) {
    set({ status: 'authenticating' });
    try {
      const res = await apiClient.signIn({ email, password });
      if (res.mfaRequired && res.mfaChallengeId) {
        set({
          status: 'unauthenticated',
          mfaPending: {
            challengeId: res.mfaChallengeId,
            email: res.user.email,
            name: res.user.name,
          },
        });
        return { mfaRequired: true };
      }
      set({
        status: 'authenticated',
        user: res.user,
        accessToken: res.accessToken ?? null,
        tenantId: res.tenantId ?? null,
        tenant: null,
        isSuperAdmin: Boolean(res.user.isSuperAdmin),
        memberships: [],
        mfaPending: null,
        billingBlocked: false,
        billingReason: null,
        graceEndsAt: null,
      });
      // Apos sign-in, /me popula memberships, tenant e billing (cookie ja setado).
      void get().bootstrap();
      return { mfaRequired: false };
    } catch (err) {
      set({ status: 'unauthenticated' });
      throw err;
    }
  },

  async verifyMfa(code) {
    const pending = get().mfaPending;
    if (!pending) {
      throw new Error('Nenhum desafio MFA pendente');
    }
    const res = await apiClient.verifyMfaLogin({
      challengeId: pending.challengeId,
      code,
    });
    set({
      status: 'authenticated',
      user: res.user,
      accessToken: res.accessToken,
      tenantId: res.tenantId ?? null,
      tenant: null,
      isSuperAdmin: Boolean(res.user.isSuperAdmin),
      memberships: [],
      mfaPending: null,
      billingBlocked: false,
      billingReason: null,
      graceEndsAt: null,
    });
    void get().bootstrap();
  },

  async signOut(reason, opts) {
    try {
      await apiClient.signOut();
    } catch {
      /* ignora — limpamos local de qualquer forma */
    }
    set({ ...initialState, status: 'unauthenticated' });
    if (opts?.broadcast !== false) {
      publish({ type: 'sign-out', reason });
    }
  },

  async switchTenant(tenantId) {
    const res = await apiClient.switchTenant(tenantId);
    set({
      accessToken: res.accessToken,
      tenantId: res.tenantId ?? tenantId,
    });
    publish({ type: 'tenant-switched', tenantId });
    // Re-popula memberships/tenant atualizados.
    void get().bootstrap();
  },

  setAccessToken(token) {
    set({ accessToken: token });
  },

  hydrateFromTokens(input) {
    set({
      status: 'authenticated',
      user: input.user,
      accessToken: input.accessToken,
      tenantId: input.tenantId,
      tenant: null,
      isSuperAdmin: Boolean(input.isSuperAdmin ?? input.user.isSuperAdmin),
      memberships: [],
      mfaPending: null,
      billingBlocked: false,
      billingReason: null,
      graceEndsAt: null,
    });
    // /me popula memberships, tenant e billing atualizados via cookie httpOnly.
    void get().bootstrap();
  },

  hardReset(_reason) {
    set({ ...initialState, status: 'unauthenticated' });
  },

  setBillingBlocked(reason) {
    // So aciona (nunca desliga) — o "402 no meio da sessao" e um sinal
    // forte de bloqueio real; desbloquear e responsabilidade do proximo
    // bootstrap() bem-sucedido (ver waitForBillingUnlock na subscription
    // page), nao deste hook reativo.
    set({ billingBlocked: true, billingReason: normalizeBillingReason(reason) });
  },

  setOnboarding(onboarding) {
    set({ onboarding });
  },
}));
